import { Fragment, useEffect, useMemo, useState } from 'react'
import { CircleHelp } from 'lucide-react'
import { FormDrawer } from '../../components/Drawer'
import { FormBody, FormCols, FormSection, Textarea } from '../../components/Form'
import FileField from '../../components/FileField'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { questionsStrings as S } from './strings'

const OPTION_KEYS = ['option1', 'option2', 'option3', 'option4']
const OPTION_KEYS_EN = ['option1En', 'option2En', 'option3En', 'option4En']

const EMPTY = {
  text: '',
  option1: '',
  option2: '',
  option3: '',
  option4: '',
  /* The English half. The legacy asks for the whole question twice — a question
     that only exists in Arabic cannot be sat by anyone using the English panel,
     and the exam still counts it. */
  textEn: '',
  option1En: '',
  option2En: '',
  option3En: '',
  option4En: '',
  imageAr: null,
  imageEn: null,
  answer: '',
  difficulty: '',
  category: '',
}

/**
 * إضافة سؤال / تعديل — the form behind the questions list's primary action.
 *
 * The button existed and did nothing; every string it needs was already sitting
 * unused in `questionsStrings.form`, which is the shape the legacy modal
 * (Views/EditQuestions) posts: the question, its four options, which of them is
 * accepted, a difficulty and a category.
 *
 * `الإجابة المختارة` offers the four options by their own text rather than by
 * number, so choosing the right answer means reading the answer — the legacy
 * asks you to map "3" onto the third box yourself. Options not yet typed are
 * left out of that list rather than offered as blanks.
 */
export default function QuestionFormDrawer({ open, question, categories, levels, onClose, onSave }) {
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!open) return
    setValues(question ? { ...EMPTY, ...question, answer: String(question.answer ?? '') } : EMPTY)
    setErrors({})
  }, [open, question])

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  /* Only options that actually carry text can be the accepted one. */
  const answerOptions = useMemo(
    () =>
      OPTION_KEYS.map((key, index) => ({ value: String(index + 1), label: values[key]?.trim() }))
        .filter((option) => option.label),
    [values],
  )

  const submit = (event) => {
    event.preventDefault()
    const next = {}
    if (!values.text.trim()) next.text = S.form.required
    if (!values.option1.trim()) next.option1 = S.form.required
    if (!values.option2.trim()) next.option2 = S.form.required
    if (!values.textEn.trim()) next.textEn = S.form.required
    if (!values.option1En.trim()) next.option1En = S.form.required
    if (!values.option2En.trim()) next.option2En = S.form.required
    if (!values.answer) next.answer = S.form.required
    /* THE TOPIC IS REQUIRED BY THE DATABASE, not just by the form. Without it the handler
       parses an empty `CategoryId` to 0 and the insert violates the foreign key — and what
       the administrator was shown was the raw EF message, "An error occurred while saving
       the entity changes. See the inner exception for details." */
    if (!values.category) next.category = S.form.required
    setErrors(next)
    if (Object.keys(next).length > 0) return
    onSave?.({ ...values, answer: Number(values.answer) })
  }

  return (
    <FormDrawer
      open={open}
      size="xl"
      formId="question-form"
      icon={CircleHelp}
      title={S.actions.create}
      subtitle={S.form.optionsPanel}
      submitLabel={S.form.save}
      cancelLabel={S.close}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormBody>
        {/* The two languages side by side rather than one under the other: an
            option and its translation have to be read together, and stacked
            they are eight fields you match up by counting. */}
        <FormCols>
          <FormRow label={S.form.questionAr} error={errors.text}>
            {(id) => (
              <Textarea
                id={id}
                rows={3}
                value={values.text}
                onChange={set('text')}
                invalid={!!errors.text}
              />
            )}
          </FormRow>
          <FormRow label={S.form.questionEn} error={errors.textEn}>
            {(id) => (
              <Textarea
                id={id}
                dir="ltr"
                rows={3}
                value={values.textEn}
                onChange={set('textEn')}
                invalid={!!errors.textEn}
              />
            )}
          </FormRow>
        </FormCols>

        <FormSection>
          <FormCols>
            {OPTION_KEYS.map((key, index) => (
              <Fragment key={key}>
                <FormRow label={`${S.columns[`option${index + 1}`]} — ${S.form.optionsAr}`} error={errors[key]}>
                  {(id) => (
                    <TextField
                      id={id}
                      value={values[key]}
                      onChange={set(key)}
                      invalid={!!errors[key]}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
                <FormRow
                  label={`${S.columns[`option${index + 1}`]} — ${S.form.optionsEn}`}
                  error={errors[OPTION_KEYS_EN[index]]}
                >
                  {(id) => (
                    <TextField
                      id={id}
                      dir="ltr"
                      value={values[OPTION_KEYS_EN[index]]}
                      onChange={set(OPTION_KEYS_EN[index])}
                      invalid={!!errors[OPTION_KEYS_EN[index]]}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
              </Fragment>
            ))}
          </FormCols>
        </FormSection>

        <FormSection>
          <FormCols>
            <FormRow label={S.form.imageAr}>
              {(id) => <FileField id={id} accept="image/*" onChange={set('imageAr')} />}
            </FormRow>
            <FormRow label={S.form.imageEn}>
              {(id) => <FileField id={id} accept="image/*" onChange={set('imageEn')} />}
            </FormRow>
          </FormCols>
        </FormSection>

        <FormSection>
          <FormCols>
            <FormRow label={S.form.chosenAnswer} error={errors.answer}>
              {(id) => (
                <Select
                  id={id}
                  value={values.answer}
                  onChange={set('answer')}
                  options={answerOptions}
                  placeholder={S.columns.answer}
                />
              )}
            </FormRow>

            <FormRow label={S.form.difficulty}>
              {(id) => (
                <Select
                  id={id}
                  value={values.difficulty}
                  onChange={set('difficulty')}
                  options={levels}
                  placeholder={S.columns.difficulty}
                />
              )}
            </FormRow>

            <FormRow label={S.form.category} error={errors.category}>
              {(id) => (
                <Select
                  id={id}
                  value={values.category}
                  onChange={set('category')}
                  options={categories}
                  placeholder={S.columns.category}
                />
              )}
            </FormRow>
          </FormCols>
        </FormSection>
      </FormBody>
    </FormDrawer>
  )
}
